<template>
  <div class="flex justify-center items-center h-full" ref="gridWrapper">
    <ul
      role="list"
      class="grid-container w-full mx-auto mt-24 h-full grid gap-2 relative"
      :class="gridClasses"
      :style="fillContainerKeepingAspectRatioCss"
    >
      <!-- To easily identify a Clinician,we update the moderatorId value and use that
        to show the big grid of the Clinician on the Participant's view
      -->
      <ClinicianTileForParticipant
        :class="{ hidden: screenShareStream.length >= 1 }"
        :remoteDisplayInfo="clinicianStream[0]"
        :userJitsiId="moderatorId"
        :setVideoAudioRefs="setVideoAudioRefs"
      />

      <!-- The stream from the local user. This is for a participant -->
      <li
        v-if="!sessionStore.hasPermissionError"
        :class="[
          !screenShareStream.length >= 1
            ? 'relative'
            : 'group block w-56 overflow-hidden z-30 absolute bottom-2 left-2',
        ]"
      >
        <StreamPlayerLocal
          :statsValue="localConnectionQuality"
          :isMinimizable="screenShareStream.length >= 1"
          class="rounded-xl camera"
          :showTitle="true"
        />
        <SpeakingIndicator :audioLevel="localAudioLevel" />
      </li>

      <template v-if="screenShareStream.length">
        <ScreenShareTile
          v-for="(screenshareProps, index) in screenShareStream"
          :index="index"
          :gridDimension="gridDimension"
          class="col-span-3 row-span-3"
          :key="screenshareProps.jitsiUserId"
          :userJitsiId="screenshareProps?.jitsiUserId"
          :remoteDisplayInfo="screenshareProps"
          :setVideoAudioRefs="setVideoAudioRefs"
          :shouldShowRemoteUserVideo="shouldShowRemoteUserVideo"
          :fillContainerKeepingAspectRatioCss="fillContainerKeepingAspectRatioCss"
        />
      </template>

      <template v-if="participantsRemoteStreams.length">
        <RemoteUsersTile
          v-for="(props, index) in participantsRemoteStreams"
          :index="index"
          :gridDimension="gridDimension"
          :class="{ hidden: screenShareStream.length >= 1 }"
          :isClinicianStream="false"
          :key="props.jitsiUserId"
          :userJitsiId="props.jitsiUserId"
          :remoteDisplayInfo="props"
          :setVideoAudioRefs="setVideoAudioRefs"
          :shouldShowRemoteUserVideo="shouldShowRemoteUserVideo"
          :moderatorId="moderatorId"
          :vuzixStreamId="vuzixStreamId"
        />
      </template>

      <SessionPausedOverlay
        v-if="sessionStore.isPhasePaused"
        :currentPhase="sessionStore.currentPhaseData.title"
        :isClinician="false"
      />
    </ul>
  </div>
</template>

<script>
import { ref, reactive, onUnmounted, onMounted, watch, computed } from 'vue';
import {
  VICE_SESSION_PHASES,
  CAMERA_TYPES,
  DISPLAY_NAME,
  USER_SESSION_ON_HOLD_KEY,
} from '@/Utils/vice-constants';
import StreamPlayerLocal from '@/Components/Session/Grid/StreamPlayerLocal.vue';
import useSessionStore from '@/Stores/useSessionStore';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import SpeakingIndicator from '@/Components/Session/Grid/SpeakingIndicator';
import useGroupCallHandlers from '@/Composables/useGroupCallHandlers';
import JitsiService from '@/Services/JitsiService';
import RemoteUsersTile from '@/Components/Session/Grid/RemoteUsersTile.vue';
import ScreenShareTile from '@/Components/Session/Grid/ScreenShareTile.vue';
import ClinicianTileForParticipant from '@/Components/Session/Grid/ClinicianTileForParticipant.vue';
import SessionPausedOverlay from '@/Components/Session/Overlays/SessionPausedOverlay.vue';

export default {
  components: {
    SessionPausedOverlay,
    StreamPlayerLocal,
    SpeakingIndicator,
    RemoteUsersTile,
    ScreenShareTile,
    ClinicianTileForParticipant,
  },
  setup() {
    const sessionStore = useSessionStore();
    const remoteUserProps = reactive({});
    const videoAudioRefs = reactive({});
    const moderatorId = ref(null);
    const vuzixStreamId = ref(null);
    const isJoined = ref(false);
    const myJitsiId = ref(null);
    const jitsiRoom = ref(null);
    const disconnect = ref(null);
    const smartphoneStreamId = ref(null);
    const localAudioLevel = ref(0);
    const localConnectionQuality = ref(0);
    const gridDimension = Array(20).fill({ cols: 1, rows: 1, start: 1 });

    if (!sessionStore.isConsultationCallStarted) {
      sessionStore.isPutOnHold = false;
      localStorage.removeItem(USER_SESSION_ON_HOLD_KEY);
    }

    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const internalCameraUser = computed(
      () => multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM],
    );

    const screenShareStream = computed(() =>
      _.filter(remoteUserProps, (_props) => /ScreenshareUser/.test(_props.userId)),
    );

    const clinicianStream = computed(() =>
      _.filter(remoteUserProps, (_props) => _props.jitsiUserId === moderatorId.value),
    );

    const participantsRemoteStreams = computed(() =>
      _.filter(
        remoteUserProps,
        (_props) =>
          _props.jitsiUserId !== moderatorId.value &&
          !/ScreenshareUser/.test(_props.userId) &&
          _props.jitsiUserId !== vuzixStreamId.value,
      ),
    );

    /**
     * When a user joins a conference, add the media track to the conference/room
     */
    const onConferenceJoined = async () => {
      const localTracks = internalCameraUser.value?.localTracks;
      const conferenceAudioTrack = internalCameraUser.value.jitsiRoom.getLocalAudioTrack();
      if (localTracks?.length) {
        isJoined.value = true;
        for (const newTrack of localTracks) {
          // Replace audio track if already exists
          if (conferenceAudioTrack === newTrack || conferenceAudioTrack?.type === newTrack?.type) {
            // eslint-disable-next-line no-await-in-loop
            await internalCameraUser.value.jitsiRoom?.replaceTrack(conferenceAudioTrack, newTrack);
          } else {
            // eslint-disable-next-line no-await-in-loop
            await internalCameraUser.value.jitsiRoom?.addTrack(newTrack);
          }
        }
      }
    };

    const resetMicState = async (shouldTurnOnMic) => {
      await multiCameraDevicesStore.resetMicState(shouldTurnOnMic);
    };

    const { events, commands } = useGroupCallHandlers({
      remoteUserProps,
      videoAudioRefs,
      room: jitsiRoom,
      onConferenceJoined,
      vuzixStreamId,
      moderatorId,
      localAudioLevel,
      localConnectionQuality,
      myJitsiId,
      resetMicState,
      smartphoneStreamId,
    });

    const checkIfAudioAndVideoTracksExist = () => {
      const trackTypes = [];

      multiCameraDevicesStore.checkPermissions().then((permitted) => {
        if (!permitted.camera || !permitted.microphone) {
          console.log('Not enough permissions. Check them now...');
          sessionStore.checkForPermissions();
        }
      });

      internalCameraUser.value?.localTracks?.forEach((track) => {
        trackTypes.push(track.type);
        if (track.stream?.active === false && sessionStore.isMyCameraOn) {
          track.unmute();
        }
      });

      return trackTypes.includes('audio') && trackTypes.includes('video');
    };

    const isReady = computed(
      () => jitsiRoom.value && (checkIfAudioAndVideoTracksExist() || sessionStore.userIsObserver),
    );

    watch(isReady, (newVal) => {
      if (newVal) {
        initializeRoom();
      }
    });

    onUnmounted(async () => {
      try {
        await disconnect.value();
      } catch (error) {
        console.error(error);
      }
    });

    onMounted(async () => {
      const { room, disconnect: disconnectJitsi } = await JitsiService().makeConnection(
        sessionStore.sessionId,
        events,
        commands,
      );
      jitsiRoom.value = room;
      disconnect.value = disconnectJitsi;
      multiCameraDevicesStore.setActiveJitsiRoom(CAMERA_TYPES.INTERNAL_WEBCAM, room);
      new ResizeObserver(calculateFillContainerKeepingAspectRatioCss).observe(gridWrapper.value);
    });

    const setVideoAudioRefs = (el, id) => {
      if (!videoAudioRefs[id]) {
        videoAudioRefs[id] = [];
      }
      if (el) {
        videoAudioRefs[id].push(el);
      }
    };

    const initializeRoom = () => {
      console.log('initializing room');
      const displayName = sessionStore.userIsObserver
        ? DISPLAY_NAME.OBSERVER
        : `${sessionStore.authUser?.name}-${sessionStore.authUser.id}`;
      jitsiRoom.value?.setDisplayName(displayName);
      const profilePhoto = sessionStore.authUser.profile_photo_url
        ? sessionStore.authUser.static_avatar_url
        : null;
      jitsiRoom.value?.setLocalParticipantProperty('avatar', profilePhoto);
      jitsiRoom.value?.setLocalParticipantProperty('isClinician', sessionStore.isClinician);
      jitsiRoom.value?.join();
      myJitsiId.value = jitsiRoom.value?.myUserId();
    };

    const gridClasses = computed(() => {
      let totalGridBoxes = Object.keys(remoteUserProps).length;
      totalGridBoxes = moderatorId.value ? totalGridBoxes : totalGridBoxes + 1;
      let classes = ' grid-rows-1 grid-cols-1';
      if (totalGridBoxes >= 1) classes = ' grid-rows-3 grid-cols-3';
      if (totalGridBoxes > 5) classes = ' grid-rows-4 grid-cols-4';
      if (totalGridBoxes > 12) classes = ' grid-rows-5 grid-cols-5';
      return classes;
    });

    // Extract to composable
    const gridWrapper = ref();
    const fillContainerKeepingAspectRatioCss = reactive({ opacity: 0 });

    const calculateFillContainerKeepingAspectRatioCss = () => {
      if (gridWrapper.value) {
        const isPortrait = gridWrapper.value.offsetWidth * 0.5625 < gridWrapper.value.offsetHeight;

        if (isPortrait) {
          fillContainerKeepingAspectRatioCss.maxWidth = `${
            gridWrapper.value.offsetHeight * 1.77777778
          }px`;
          fillContainerKeepingAspectRatioCss.opacity = 1;
          fillContainerKeepingAspectRatioCss.height = 'calc(100vh - 90px)';
        } else {
          fillContainerKeepingAspectRatioCss.maxWidth = `${
            gridWrapper.value.offsetHeight * 1.77777778
          }px`;
          fillContainerKeepingAspectRatioCss.opacity = 1;
          fillContainerKeepingAspectRatioCss.height = 'calc(100vh - 90px)';
        }
      }
    };

    const shouldShowRemoteUserVideo = (participantJitsiId) =>
      remoteUserProps?.[participantJitsiId]?.video &&
      !remoteUserProps?.[participantJitsiId]?.isVideoHidden &&
      !sessionStore.isPutOnHold;

    return {
      myJitsiId,
      gridClasses,
      moderatorId,
      sessionStore,
      gridDimension,
      vuzixStreamId,
      remoteUserProps,
      setVideoAudioRefs,
      VICE_SESSION_PHASES,
      multiCameraDevicesStore,
      shouldShowRemoteUserVideo,
      fillContainerKeepingAspectRatioCss,
      gridWrapper,
      localAudioLevel,
      localConnectionQuality,
      screenShareStream,
      clinicianStream,
      participantsRemoteStreams,
    };
  },
};
</script>

<style lang="scss" scoped>
.grid-container {
  // min-height: calc(100vh - 172px);
  opacity: 0;
}
.vuzixToggleButton {
  z-index: 2;
}

.text-shadow {
  text-shadow: 0 0 2px black;
}

.shadow-filter {
  filter: drop-shadow(0 0 2px black);
}
</style>
